import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, CheckCircle2, AlertCircle, ChevronRight, ChevronLeft } from "lucide-react";
import { api, fmt } from "@/lib/api";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

export default function Accounts() {
  const navigate = useNavigate();
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const { data } = useQuery({ queryKey: ["accounts"], queryFn: async () => (await api.get("/admin/accounts")).data, refetchInterval: 6000 });
  const { data: report } = useQuery({ queryKey: ["monthly", ym], queryFn: async () => (await api.get("/admin/reports/monthly", { params: ym })).data, refetchInterval: 8000 });
  const shiftMonth = (d) => setYm((p) => { const dt = new Date(p.year, p.month - 1 + d, 1); return { year: dt.getFullYear(), month: dt.getMonth() + 1 }; });
  const { data: yearly } = useQuery({ queryKey: ["yearly", ym.year], queryFn: async () => (await api.get("/admin/reports/yearly", { params: { year: ym.year } })).data, refetchInterval: 10000 });

  return (
    <div className="space-y-5" data-testid="accounts-page">
      <h1 className="font-display text-2xl">الحسابات</h1>

      <div className="card-lux p-5" data-testid="monthly-report">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm text-gold">التقرير الشهري</h2>
          <div className="flex items-center gap-2" data-testid="report-month">
            <button onClick={() => shiftMonth(1)} data-testid="report-next" className="w-8 h-8 rounded-full glass flex items-center justify-center text-gold hover:bg-navy3 transition-colors" aria-label="الشهر التالي"><ChevronLeft size={16} /></button>
            <span className="text-sm text-offwhite min-w-[120px] text-center font-medium">{AR_MONTHS[ym.month - 1]} {ym.year}</span>
            <button onClick={() => shiftMonth(-1)} data-testid="report-prev" className="w-8 h-8 rounded-full glass flex items-center justify-center text-gold hover:bg-navy3 transition-colors" aria-label="الشهر السابق"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><p className="text-xs text-muted2 mb-1">عدد الحجوزات</p><p className="text-offwhite font-bold text-lg">{report?.count || 0}</p></div>
          <div><p className="text-xs text-muted2 mb-1">قيمة الحجوزات</p><p className="gold-text font-bold text-sm">{fmt(report?.total_value)}</p></div>
          <div><p className="text-xs text-muted2 mb-1">المستلم بالشهر</p><p className="text-green-400 font-bold text-sm">{fmt(report?.received)}</p></div>
        </div>
      </div>

      <div className="card-lux p-5" data-testid="yearly-report">
        <h2 className="text-sm text-gold mb-4">الملخص السنوي — {ym.year}</h2>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div><p className="text-xs text-muted2 mb-1">عدد الحجوزات</p><p className="text-offwhite font-bold text-lg">{yearly?.count || 0}</p></div>
          <div><p className="text-xs text-muted2 mb-1">قيمة الحجوزات</p><p className="gold-text font-bold text-sm">{fmt(yearly?.total_value)}</p></div>
          <div><p className="text-xs text-muted2 mb-1">المستلم بالسنة</p><p className="text-green-400 font-bold text-sm">{fmt(yearly?.received)}</p></div>
          <div><p className="text-xs text-muted2 mb-1">المتبقي</p><p className="text-red-400 font-bold text-sm">{fmt(yearly?.total_remaining)}</p></div>
        </div>
      </div>

      <div className="grid gap-3">
        <Big icon={TrendingUp} label="إجمالي قيمة الحجوزات المؤكدة" value={fmt(data?.total_value)} />
        <div className="grid grid-cols-2 gap-3">
          <Card icon={CheckCircle2} label="المبالغ المستلمة" value={fmt(data?.total_paid)} color="green" />
          <Card icon={AlertCircle} label="المبالغ المتبقية" value={fmt(data?.total_remaining)} color="red" />
        </div>
      </div>
      <div>
        <h2 className="text-sm text-muted2 mb-2">حجوزات عليها مبالغ متبقية</h2>
        <div className="space-y-2">
          {(data?.with_remaining || []).map((b) => (
            <button key={b.id} onClick={() => navigate(`/admin/booking/${b.id}`)} className="card-lux p-4 w-full flex justify-between items-center text-start hover:bg-navy3/40 transition-colors">
              <div>
                <p className="text-offwhite">{b.name}</p>
                <p className="text-xs text-muted2">{b.booking_number} · {b.date}</p>
              </div>
              <span className="text-red-400 text-sm font-semibold">{fmt(b.remaining)}</span>
            </button>
          ))}
          {!data?.with_remaining?.length && <p className="text-muted2/60 text-sm card-lux p-4">لا توجد مبالغ متبقية.</p>}
        </div>
      </div>
    </div>
  );
}

const Big = ({ icon: Icon, label, value }) => (
  <div className="card-lux p-5">
    <Icon size={22} className="text-gold mb-2" />
    <p className="text-xs text-muted2">{label}</p>
    <p className="gold-text font-bold text-2xl mt-1">{value}</p>
  </div>
);
const Card = ({ icon: Icon, label, value, color }) => (
  <div className="card-lux p-4">
    <Icon size={20} className={color === "green" ? "text-green-400" : "text-red-400"} />
    <p className="text-xs text-muted2 mt-2">{label}</p>
    <p className="text-offwhite font-bold text-lg mt-0.5">{value}</p>
  </div>
);
